import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import WhatsAppButton from "@/components/WhatsAppButton";
import Home from "@/pages/Home";
import Trails from "@/pages/Trails";
import TrailDetail from "@/pages/TrailDetail";
import Community from "@/pages/Community";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import ShopInfo from "@/pages/ShopInfo";
import NotFound from "@/pages/NotFound";
import { CartProvider } from "@/context/CartContext";
import CartDrawer from "@/components/shop/CartDrawer";
import Arcade from "@/pages/Arcade";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Admin from "@/pages/Admin";
import AdminSettings from "@/pages/AdminSettings";
import AdminNewsletter from "@/pages/AdminNewsletter";
import Eventi from "@/pages/Eventi";
import EventDetail from "@/pages/EventDetail";
import AdminEvents from "@/pages/AdminEvents";
import Privacy from "@/pages/Privacy";
import CookiePolicy from "@/pages/CookiePolicy";
import AdminStats from "@/pages/AdminStats";
import Guides from "@/pages/Guides";
import GuideDetail from "@/pages/GuideDetail";
import AdminGuides from "@/pages/AdminGuides";
import AdminQuest from "@/pages/AdminQuest";
import RideCallPage from "@/pages/RideCall";
import Stay from "@/pages/Stay";
import AdminStay from "@/pages/AdminStay";
import AdminModeration from "@/pages/AdminModeration";
import AdminSocialPost from "@/pages/AdminSocialPost";
import Video from "@/pages/Video";
import Abnormal from "@/pages/Abnormal";
import useAnalytics from "@/hooks/useAnalytics";
import { useEffect } from "react";
import { OffFeedProvider } from "@/context/OffFeedContext";
import OffFeed from "@/components/OffFeed";

/* Scroll to top on route change (hash-less). Hash scrolling is handled per page. */
function ScrollToTop() {
    const { pathname, hash } = useLocation();
    useEffect(() => { if (!hash) window.scrollTo(0, 0); }, [pathname, hash]);
    return null;
}

/* Hidden detail — welcome message for curious minds who open DevTools.
   Logged once per session. */
function useConsoleWelcome() {
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (window.__ABNORMAL_WELCOMED__) return;
        window.__ABNORMAL_WELCOMED__ = true;
        const style = "color:#FF3B3B;font-weight:bold;font-family:monospace;letter-spacing:2px;";
        const dim = "color:#888;font-family:monospace;letter-spacing:1.5px;";
        console.log("%c// ABNORMAL WORLD", style);
        console.log("%cThe brain isn't abnormal. Ideas are.\nMonte Argentario · Toscana · Italia\n// stay curious — info@abnormalworld.it", dim);
    }, []);
}

function RouteShell() {
    useAnalytics();
    useConsoleWelcome();
    return (
        <>
            <Navbar />
            <ScrollToTop />
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/trail" element={<Trails />} />
                <Route path="/trail/:slug" element={<TrailDetail />} />
                <Route path="/abnormal" element={<Abnormal />} />
                <Route path="/storia" element={<Navigate to="/abnormal#story" replace />} />
                <Route path="/community" element={<Community />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/info" element={<ShopInfo />} />
                <Route path="/shop/:handle" element={<ProductDetail />} />
                <Route path="/arcade" element={<Arcade />} />
                <Route path="/arcade/sfide" element={<Arcade />} />
                <Route path="/arcade/stage-1" element={<Arcade />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/settings" element={<AdminSettings />} />
                <Route path="/admin/newsletter" element={<AdminNewsletter />} />
                <Route path="/admin/stats" element={<AdminStats />} />
                <Route path="/admin/guide" element={<AdminGuides />} />
                <Route path="/admin/quest" element={<AdminQuest />} />
                <Route path="/eventi" element={<Eventi />} />
                <Route path="/eventi/:slug" element={<EventDetail />} />
                <Route path="/admin/events" element={<AdminEvents />} />
                <Route path="/guide" element={<Guides />} />
                <Route path="/guide/:slug" element={<GuideDetail />} />
                <Route path="/ride-call" element={<RideCallPage />} />
                <Route path="/video" element={<Video />} />
                <Route path="/stay" element={<Stay />} />
                <Route path="/admin/stay" element={<AdminStay />} />
                <Route path="/admin/moderation" element={<AdminModeration />} />
                <Route path="/admin/social-post" element={<AdminSocialPost />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
            <Footer />
            <CartDrawer />
            <CookieBanner />
            <WhatsAppButton />
            <OffFeed />
            <Toaster position="top-right" theme="dark" mobileOffset={{ bottom: 104, top: 16, left: 16, right: 16 }} />
        </>
    );
}

function App() {
    return (
        <div className="App">
            <HelmetProvider>
                <LanguageProvider>
                    <AuthProvider>
                        <OffFeedProvider>
                        <CartProvider>
                            <BrowserRouter>
                                <RouteShell />
                            </BrowserRouter>
                        </CartProvider>
                        </OffFeedProvider>
                    </AuthProvider>
                </LanguageProvider>
            </HelmetProvider>
        </div>
    );
}

export default App;
